import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, TrendingUp, TrendingDown, Minus } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";

export function Cumplimiento() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Tablero de Cumplimiento"
        subtitle="Visualización de metas vs. realidad"
      />

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Cumplimiento General</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-3xl font-bold">92%</div>
              <div className="flex items-center text-sm text-success">
                <TrendingUp className="h-4 w-4" />
                +5%
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Metas Activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">24</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Metas Cumplidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-3xl font-bold">18</div>
              <Badge variant="default">75%</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Alertas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-3xl font-bold">3</div>
              <Badge variant="secondary">Pendientes</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="semanal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="semanal">Semanal</TabsTrigger>
          <TabsTrigger value="mensual">Mensual</TabsTrigger>
          <TabsTrigger value="anual">Anual</TabsTrigger>
        </TabsList>

        <TabsContent value="semanal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cumplimiento Semanal por Área</CardTitle>
              <CardDescription>Semana del 8 al 14 de Enero, 2024</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { area: "Terreno A", meta: 100, actual: 94, tendencia: "up" },
                  { area: "Operaciones", meta: 100, actual: 88, tendencia: "down" },
                  { area: "Construcción", meta: 100, actual: 96, tendencia: "up" },
                  { area: "Flota", meta: 100, actual: 100, tendencia: "stable" },
                  { area: "Seguridad", meta: 100, actual: 85, tendencia: "down" },
                ].map((item, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{item.area}</span>
                        {item.tendencia === "up" && <TrendingUp className="h-4 w-4 text-success" />}
                        {item.tendencia === "down" && <TrendingDown className="h-4 w-4 text-destructive" />}
                        {item.tendencia === "stable" && <Minus className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                          {item.actual}% de {item.meta}%
                        </span>
                        <Badge variant={item.actual >= 90 ? "default" : "secondary"}>
                          {item.actual >= 90 ? "Cumplido" : "Pendiente"}
                        </Badge>
                      </div>
                    </div>
                    <div className="h-2 w-full rounded-full bg-secondary">
                      <div
                        className={`h-2 rounded-full ${
                          item.actual >= 90 ? "bg-primary" : "bg-warning-bar"
                        }`}
                        style={{ width: `${item.actual}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Top 3 Mejores
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { area: "Flota", cumplimiento: 100 },
                  { area: "Construcción", cumplimiento: 96 },
                  { area: "Terreno A", cumplimiento: 94 },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                        {idx + 1}
                      </div>
                      <span className="font-medium">{item.area}</span>
                    </div>
                    <Badge variant="default">{item.cumplimiento}%</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-destructive" />
                  Requieren Atención
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { area: "Seguridad", cumplimiento: 85, problema: "Falta 3 ART" },
                  { area: "Operaciones", cumplimiento: 88, problema: "Checks pendientes" },
                ].map((item, idx) => (
                  <div key={idx} className="rounded-lg border border-warning/25 bg-warning-bg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.area}</p>
                        <p className="text-sm text-muted-foreground">{item.problema}</p>
                      </div>
                      <Badge variant="secondary">{item.cumplimiento}%</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="mensual">
          <Card>
            <CardContent className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">Vista mensual - Gráficos disponibles próximamente</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="anual">
          <Card>
            <CardContent className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">Vista anual - Gráficos disponibles próximamente</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
